import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Brain, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  CreditCard,
  Zap,
  PieChart as PieChartIcon
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { entryCountsInOperationalFinance } from "@/utils/financialOperationalImpact";

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  payment_notes?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number;
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface FinancialDashboardProps {
  entries: FinancialEntry[];
  recurringEntries: RecurringEntry[];
}

const FinancialDashboard = ({ entries, recurringEntries }: FinancialDashboardProps) => {
  const [allEntries, setAllEntries] = useState<FinancialEntry[]>([]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para gerar entradas virtuais baseadas nas recorrentes
  const generateVirtualEntries = async () => {
    const virtualEntries: FinancialEntry[] = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // Mês 1-12
    const currentYear = currentDate.getFullYear();

    // Buscar pagamentos existentes para o mês atual
    const { data: existingPayments } = await supabase
      .from('recurring_payments')
      .select('recurring_entry_id')
      .eq('payment_month', currentMonth)
      .eq('payment_year', currentYear);

    const paidRecurringIds = new Set(existingPayments?.map(p => p.recurring_entry_id) || []);

    recurringEntries
      .filter(entry => entry.is_active && !paidRecurringIds.has(entry.id))
      .forEach(entry => {
        // Criar entrada virtual para o mês atual
        const virtualEntry: FinancialEntry = {
          id: `virtual-${entry.id}-${currentMonth - 1}-${currentYear}`, // currentMonth - 1 para manter compatibilidade com getMonth()
          description: `${entry.description} (Recorrente)`,
          amount: entry.amount,
          entry_type: entry.entry_type,
          category: entry.category,
          status: 'Pendente', // Entradas recorrentes são consideradas pendentes até confirmadas
          created_at: new Date(currentYear, currentMonth - 1, entry.due_day).toISOString(),
          clients: entry.clients,
          cases: entry.cases
        };
        virtualEntries.push(virtualEntry);
      });

    return virtualEntries;
  };

  // Função para atualizar as entradas combinadas
  const updateAllEntries = async () => {
    const virtualEntries = await generateVirtualEntries();
    const operational = entries.filter(entryCountsInOperationalFinance);
    setAllEntries([...operational, ...virtualEntries]);
  };

  // Atualizar entradas combinadas quando entries ou recurringEntries mudarem
  useEffect(() => {
    if (entries.length > 0 || recurringEntries.length > 0) {
      updateAllEntries();
    }
  }, [entries, recurringEntries]);

  // Análise inteligente dos dados
  const analyzeFinancialHealth = () => {
    const revenues = allEntries.filter(e => e.entry_type === 'receita');
    const expenses = allEntries.filter(e => e.entry_type === 'despesa');
    
    const totalRevenue = revenues.reduce((sum, entry) => sum + entry.amount, 0);
    const totalExpense = expenses.reduce((sum, entry) => sum + entry.amount, 0);
    const netProfit = totalRevenue - totalExpense;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue * 100) : 0;

    // Análise de tendências (últimos 6 meses vs 6 meses anteriores)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const yearAgo = new Date();
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);

    const recentRevenues = revenues.filter(e => new Date(e.created_at) > sixMonthsAgo);
    const oldRevenues = revenues.filter(e => {
      const date = new Date(e.created_at);
      return date > yearAgo && date <= sixMonthsAgo;
    });

    const recentTotal = recentRevenues.reduce((sum, e) => sum + e.amount, 0);
    const oldTotal = oldRevenues.reduce((sum, e) => sum + e.amount, 0);
    const growthRate = oldTotal > 0 ? ((recentTotal - oldTotal) / oldTotal * 100) : 0;

    // Análise de saúde financeira
    let healthScore = 0;
    let healthStatus = "Crítico";
    let healthColor = "text-destructive";

    if (profitMargin > 20) healthScore += 30;
    else if (profitMargin > 10) healthScore += 20;
    else if (profitMargin > 0) healthScore += 10;

    if (growthRate > 10) healthScore += 25;
    else if (growthRate > 5) healthScore += 15;
    else if (growthRate > 0) healthScore += 10;

    const paidRatio = revenues.filter(e => e.status === 'Pago').length / revenues.length * 100;
    if (paidRatio > 80) healthScore += 25;
    else if (paidRatio > 60) healthScore += 15;
    else if (paidRatio > 40) healthScore += 10;

    const overdueEntries = allEntries.filter(e => {
      if (e.payment_date && e.status !== 'Pago') {
        return new Date(e.payment_date) < new Date();
      }
      return false;
    });
    const overdueRatio = overdueEntries.length / allEntries.length * 100;
    if (overdueRatio < 5) healthScore += 20;
    else if (overdueRatio < 15) healthScore += 10;

    if (healthScore >= 80) {
      healthStatus = "Excelente";
      healthColor = "text-tertiary";
    } else if (healthScore >= 60) {
      healthStatus = "Bom";
      healthColor = "text-secondary";
    } else if (healthScore >= 40) {
      healthStatus = "Regular";
      healthColor = "text-warning";
    }

    return {
      healthScore,
      healthStatus,
      healthColor,
      profitMargin,
      growthRate,
      paidRatio,
      overdueRatio,
      totalRevenue,
      totalExpense,
      netProfit
    };
  };

  // Insights inteligentes
  const generateInsights = () => {
    const analysis = analyzeFinancialHealth();
    const insights = [];

    // Insights de receita
    if (analysis.growthRate > 15) {
      insights.push({
        type: "success",
        title: "Crescimento Acelerado",
        description: `Suas receitas cresceram ${analysis.growthRate.toFixed(1)}% nos últimos 6 meses. Mantenha essa estratégia!`,
        icon: TrendingUp,
        priority: "high"
      });
    } else if (analysis.growthRate < 0) {
      insights.push({
        type: "warning",
        title: "Queda na Receita",
        description: `Receitas diminuíram ${Math.abs(analysis.growthRate).toFixed(1)}%. Analise as causas e ajuste a estratégia.`,
        icon: TrendingDown,
        priority: "high"
      });
    }

    // Insights de margem de lucro
    if (analysis.profitMargin < 10) {
      insights.push({
        type: "critical",
        title: "Margem Baixa",
        description: `Margem de lucro de ${analysis.profitMargin.toFixed(1)}% está abaixo do ideal. Revise custos.`,
        icon: AlertCircle,
        priority: "high"
      });
    }

    // Insights de cobrança
    if (analysis.paidRatio < 70) {
      insights.push({
        type: "warning",
        title: "Taxa de Cobrança Baixa",
        description: `Apenas ${analysis.paidRatio.toFixed(1)}% das receitas foram pagas. Melhore o processo de cobrança.`,
        icon: CreditCard,
        priority: "medium"
      });
    }

    // Insights de inadimplência
    if (analysis.overdueRatio > 10) {
      insights.push({
        type: "critical",
        title: "Alto Índice de Inadimplência",
        description: `${analysis.overdueRatio.toFixed(1)}% das entradas estão vencidas. Ação urgente necessária.`,
        icon: Clock,
        priority: "high"
      });
    }

    // Insights positivos
    if (analysis.healthScore >= 80) {
      insights.push({
        type: "success",
        title: "Saúde Financeira Excelente",
        description: "Todos os indicadores estão saudáveis. Continue assim!",
        icon: CheckCircle,
        priority: "low"
      });
    }

    return insights.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority as keyof typeof priorityOrder] - priorityOrder[a.priority as keyof typeof priorityOrder];
    });
  };

  // Recomendações acionáveis
  const generateRecommendations = () => {
    const analysis = analyzeFinancialHealth();
    const recommendations = [];

    if (analysis.profitMargin < 15) {
      recommendations.push({
        title: "Otimize Suas Despesas",
        description: "Revise despesas recorrentes e negocie melhores condições com fornecedores.",
        action: "Analisar categorias de despesas",
        impact: "Alto",
        effort: "Médio"
      });
    }

    if (analysis.paidRatio < 80) {
      recommendations.push({
        title: "Melhore o Processo de Cobrança",
        description: "Implemente lembretes automáticos e políticas de cobrança mais eficazes.",
        action: "Configurar sistema de cobrança",
        impact: "Alto",
        effort: "Baixo"
      });
    }

    if (analysis.growthRate < 5) {
      recommendations.push({
        title: "Estratégias de Crescimento",
        description: "Diversifique serviços e explore novos nichos de mercado.",
        action: "Planejar expansão",
        impact: "Alto",
        effort: "Alto"
      });
    }

    if (analysis.overdueRatio > 5) {
      recommendations.push({
        title: "Reduza a Inadimplência",
        description: "Estabeleça políticas de pagamento mais rígidas e analise o perfil dos clientes.",
        action: "Revisar política de crédito",
        impact: "Médio",
        effort: "Médio"
      });
    }

    return recommendations;
  };

  const analysis = analyzeFinancialHealth();
  const insights = generateInsights();
  const recommendations = generateRecommendations();

  // Categorias top por valor
  const topCategories = () => {
    const categoryTotals: { [key: string]: number } = {};
    allEntries.forEach(entry => {
      if (!categoryTotals[entry.category]) {
        categoryTotals[entry.category] = 0;
      }
      categoryTotals[entry.category] += entry.amount;
    });

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  };

  const RevenueExpensePieChart = ({ totalRevenue, totalExpense }: { totalRevenue: number; totalExpense: number }) => {
    const data = [
      { name: 'Receitas', value: totalRevenue, color: 'hsl(var(--tertiary))' },
      { name: 'Despesas', value: totalExpense, color: 'hsl(var(--destructive))' },
    ];

    return (
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            fill="#8884d8"
            paddingAngle={5}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatCurrency(value)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const CategoryBarChart = ({ data }: { data: { category: string; amount: number }[] }) => {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical" margin={{ left: 80 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" tickFormatter={formatCurrency} fontSize={11} tickLine={false} axisLine={false} stroke="#94a3b8" />
          <YAxis type="category" dataKey="category" fontSize={12} tickLine={false} axisLine={false} width={110} stroke="#475569" />
          <Tooltip formatter={(value: number) => formatCurrency(value)} cursor={{ fill: '#faf7f2' }} />
          <Bar dataKey="amount" fill="#c4ab83" radius={[0, 6, 6, 0]} barSize={18} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Anel da nota de saúde financeira
  const score = Math.max(0, Math.min(100, analysis.healthScore));
  const ringRadius = 52;
  const ringLength = 2 * Math.PI * ringRadius;
  const scoreColor = score >= 70 ? '#10b981' : score >= 40 ? '#c4ab83' : '#ef4444';
  const flowTotal = analysis.totalRevenue + analysis.totalExpense;
  const revenueShare = flowTotal > 0 ? (analysis.totalRevenue / flowTotal) * 100 : 50;

  const metricTiles = [
    {
      label: 'Margem',
      value: `${analysis.profitMargin.toFixed(1)}%`,
      hint: 'do que entra, quanto sobra',
      tone: analysis.profitMargin < 10 ? 'text-amber-600' : 'text-slate-900',
    },
    {
      label: 'Crescimento',
      value: `${analysis.growthRate > 0 ? '+' : ''}${analysis.growthRate.toFixed(1)}%`,
      hint: 'receitas vs. período anterior',
      tone: analysis.growthRate < 0 ? 'text-red-600' : analysis.growthRate > 0 ? 'text-emerald-600' : 'text-slate-900',
    },
    {
      label: 'Taxa de cobrança',
      value: `${analysis.paidRatio.toFixed(1)}%`,
      hint: 'receitas já recebidas',
      tone: analysis.paidRatio < 70 ? 'text-amber-600' : 'text-slate-900',
    },
    {
      label: 'Inadimplência',
      value: `${analysis.overdueRatio.toFixed(1)}%`,
      hint: 'receitas vencidas',
      tone: analysis.overdueRatio > 10 ? 'text-red-600' : 'text-slate-900',
    },
  ];

  const toneStyles: Record<string, { bar: string; text: string; label: string }> = {
    success: { bar: 'bg-emerald-500', text: 'text-emerald-700', label: 'Positivo' },
    warning: { bar: 'bg-amber-500', text: 'text-amber-700', label: 'Atenção' },
    critical: { bar: 'bg-red-500', text: 'text-red-700', label: 'Crítico' },
  };
  const segmentTrigger =
    'rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm';

  return (
    <div className="space-y-5">
      {/* Saúde financeira */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white animate-in fade-in slide-in-from-bottom-1 duration-300">
        <div className="relative overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-5 sm:px-6">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-28 w-28 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <p className="relative text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Saúde financeira</p>
          <p className="relative mt-1 text-2xl font-semibold tracking-tight text-slate-900">Como está o escritório</p>
        </div>

        <div className="grid grid-cols-1 gap-6 p-5 sm:p-6 lg:grid-cols-[auto_1fr]">
          {/* Nota */}
          <div className="flex flex-col items-center justify-center gap-3 lg:pr-6">
            <div className="relative h-36 w-36">
              <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                <circle cx="60" cy="60" r={ringRadius} fill="none" stroke="#f1f5f9" strokeWidth="10" />
                <circle
                  cx="60"
                  cy="60"
                  r={ringRadius}
                  fill="none"
                  stroke={scoreColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={ringLength}
                  strokeDashoffset={ringLength * (1 - score / 100)}
                  style={{ transition: 'stroke-dashoffset 800ms ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-semibold tabular-nums tracking-tight text-slate-900">{score}</span>
                <span className="text-[11px] text-slate-400">de 100</span>
              </div>
            </div>
            <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: `${scoreColor}1a`, color: scoreColor }}>
              {analysis.healthStatus}
            </span>
          </div>

          <div className="space-y-5">
            {/* Indicadores */}
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 lg:grid-cols-4">
              {metricTiles.map((tile) => (
                <div key={tile.label} className="bg-white px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
                  <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${tile.tone}`}>{tile.value}</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">{tile.hint}</p>
                </div>
              ))}
            </div>

            {/* Receitas x despesas */}
            <div>
              <div className="mb-2 flex items-end justify-between text-xs">
                <span>
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Receitas</span>
                  <span className="text-base font-semibold tabular-nums text-emerald-600">{formatCurrency(analysis.totalRevenue)}</span>
                </span>
                <span className="text-right">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Despesas</span>
                  <span className="text-base font-semibold tabular-nums text-red-600">{formatCurrency(analysis.totalExpense)}</span>
                </span>
              </div>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div className="bg-emerald-500 transition-all duration-700" style={{ width: `${revenueShare}%` }} />
                <div className="bg-red-400 transition-all duration-700" style={{ width: `${100 - revenueShare}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Resultado:{' '}
                <span className={`font-semibold ${analysis.totalRevenue - analysis.totalExpense >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(analysis.totalRevenue - analysis.totalExpense)}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Insights, recomendações e categorias */}
      <Tabs defaultValue="insights" className="w-full">
        <TabsList className="mb-3 grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
          <TabsTrigger value="insights" className={segmentTrigger}>
            Insights
          </TabsTrigger>
          <TabsTrigger value="recommendations" className={segmentTrigger}>
            Recomendações
          </TabsTrigger>
          <TabsTrigger value="categories" className={segmentTrigger}>
            Top categorias
          </TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="mt-0">
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            {insights.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-400">Nenhum alerta no momento.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {insights.map((insight, index) => {
                  const Icon = insight.icon;
                  const tone = toneStyles[insight.type] || toneStyles.warning;
                  return (
                    <div key={index} className="flex items-start gap-4 px-5 py-4">
                      <span className={`mt-1 h-9 w-1 shrink-0 rounded-full ${tone.bar}`} />
                      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.text}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900">{insight.title}</p>
                          {insight.priority === 'high' && (
                            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">Prioridade alta</span>
                          )}
                        </div>
                        <p className="mt-0.5 text-sm text-slate-500">{insight.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recommendations" className="mt-0">
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            {recommendations.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-400">Nenhuma recomendação no momento.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {recommendations.map((rec, index) => (
                  <div key={index} className="px-5 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{rec.title}</p>
                      <div className="flex gap-1.5 text-[10px] font-semibold">
                        <span className="rounded-full bg-[#c4ab83]/15 px-2 py-0.5 text-[#7a6440]">Impacto {String(rec.impact).toLowerCase()}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">Esforço {String(rec.effort).toLowerCase()}</span>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{rec.description}</p>
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                      Próxima ação: {rec.action}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-0">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Principais categorias por valor</p>
            <CategoryBarChart data={topCategories()} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialDashboard;
